import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  Home as HomeIcon,
  Logout as LogoutIcon,
  ManageAccounts as ManageAccountsIcon,
  Menu,
  Paid as PaidIcon,
  Payment as PaymentIcon,
  ReportProblem as ReportProblemIcon,
  SettingsSuggest as SettingsSuggestIcon,
  Timeline as TimelineIcon,
  AutoAwesome as AutoAwesomeIcon,
  CreditCard as RechargeIcon,
  ContactMail as ContactMailIcon,
  VerifiedUser as VerifiedUserIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { toast } from 'react-toastify';
import HeaderProfileMenu from './HeaderProfileMenu';

interface AdminLayoutProps {
  children: React.ReactNode;
  currentPage?: string;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, currentPage }) => {
  const navigate = useNavigate();
  const { logout, user, userRole } = useAuthStore();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const menuItems = [
    {
      label: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/admin',
      id: 'dashboard',
    },
    {
      label: 'Recharge Management',
      icon: <RechargeIcon />,
      path: '/admin/recharge-management',
      id: 'recharge',
    },
    {
      label: 'Recharge History',
      icon: <PaymentIcon />,
      path: '/admin/recharge-history',
      id: 'recharge-history',
    },
    {
      label: 'Profile Reviews',
      icon: <VerifiedUserIcon />,
      path: '/admin/profile-reviews',
      id: 'profile-reviews',
    },
    {
      label: 'Reports',
      icon: <ReportProblemIcon />,
      path: '/admin/reports',
      id: 'reports',
    },
    {
      label: 'Contact Inbox',
      icon: <ContactMailIcon />,
      path: '/admin/contact-inquiries',
      id: 'contact-inquiries',
    },
    {
      label: 'User Management',
      icon: <ManageAccountsIcon />,
      path: '/admin/users',
      id: 'user-management',
    },
    {
      label: 'Inactive Users',
      icon: <ManageAccountsIcon />,
      path: '/admin/inactive-users',
      id: 'inactive-users',
    },
    {
      label: 'Sub-admin Accounts',
      icon: <ManageAccountsIcon />,
      path: '/admin/subadmins',
      id: 'subadmins',
      superAdminOnly: true,
    },
    {
      label: 'Activation Status',
      icon: <VerifiedUserIcon />,
      path: '/admin/activation-status',
      id: 'activation-status',
    },
    {
      label: 'Account Rules',
      icon: <PaidIcon />,
      path: '/admin/wallet-rules',
      id: 'account-rules',
    },
    {
      label: 'Filter Options',
      icon: <SettingsSuggestIcon />,
      path: '/admin/filter-options',
      id: 'filter-options',
    },
    {
      label: 'Registration Fields',
      icon: <SettingsSuggestIcon />,
      path: '/admin/registration-fields',
      id: 'registration-fields',
    },
    {
      label: 'Daily Recommendations',
      icon: <AutoAwesomeIcon />,
      path: '/admin/daily-recommendations',
      id: 'daily-recommendations',
    },
    {
      label: 'User Control Center',
      icon: <TimelineIcon />,
      path: '/admin/user-control-center',
      id: 'user-control-center',
    },
    // Payment Gateway hidden for now - can be re-enabled later
    // {
    //   label: 'Payment Gateway',
    //   icon: <PaymentIcon />,
    //   path: '/admin/payment-gateway',
    //   id: 'gateway',
    // },
  ].filter((item) => {
    const currentRole = String(userRole || user?.role || '').toUpperCase();
    if (item.id === 'inactive-users' && currentRole !== 'SUPER_ADMIN') return false;
    if (item.id === 'dashboard') return true;
    if (currentRole !== 'SUPPORT_ADMIN' || !user?.admin_permissions?.length) return true;
    return user?.admin_permissions?.includes(item.id) ?? false;
  });

  const drawer = (
    <Box
      sx={{
        minHeight: '100%',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #6d000f 0%, #810014 100%)',
        color: '#fffaf3',
      }}
    >
      <Box sx={{ p: 2.2, borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
        <Box component="img" src="/maandvo-logo.png" alt="Maandvo" sx={{ width: '100%', maxWidth: 170, display: 'block', mb: 1.2 }} />
        <Typography sx={{ fontSize: '0.78rem', color: '#f8dcb0', fontWeight: 700, letterSpacing: '0.06em' }}>
          ADMIN CONTROL CENTER
        </Typography>
      </Box>

      <List sx={{ flexGrow: 1, p: 1.2 }}>
        {menuItems.map((item) => (
          <ListItemButton
            key={item.id}
            onClick={() => {
              navigate(item.path);
              setMobileOpen(false);
            }}
            selected={currentPage === item.id}
            sx={{
              mb: 0.8,
              borderRadius: 1.6,
              color: '#fff6ea',
              backgroundColor:
                currentPage === item.id ? 'rgba(255, 255, 255, 0.14)' : 'transparent',
              borderLeft: currentPage === item.id ? '3px solid #ffcb74' : '3px solid transparent',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: currentPage === item.id ? '#ffd28a' : '#fff2dc',
                minWidth: 40,
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: currentPage === item.id ? 800 : 500,
                  color: currentPage === item.id ? '#fff' : '#fff6ea',
                },
              }}
            />
          </ListItemButton>
        ))}
      </List>

      <Box sx={{ p: 1.4, pt: 0.4 }}>
        <Paper sx={{ mb: 1.2, p: 1.3, backgroundColor: 'rgba(255,255,255,0.11)', borderRadius: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#ffe4bc' }}>
            LOGGED IN AS
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.4, color: '#fff' }}>
            {user?.first_name} {user?.last_name}
          </Typography>
          <Typography variant="caption" sx={{ color: '#fff1dc' }}>
            {user?.email}
          </Typography>
        </Paper>

        <ListItemButton
          onClick={() => {
            navigate('/my-profile');
            setMobileOpen(false);
          }}
          sx={{
            borderRadius: 1.5,
            mb: 0.8,
            color: '#fff6ea',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: '#fff2dc' }}>
            <HomeIcon />
          </ListItemIcon>
          <ListItemText primary="User Dashboard" />
        </ListItemButton>

        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 1.5,
            color: '#ffd2d2',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
            },
          }}
        >
          <ListItemIcon sx={{ color: '#ffd2d2', minWidth: 40 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </Box>
    </Box>
  );

  const drawerWidth = 280;

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #fff7eb 0%, #fffdf8 100%)' }}>
      <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1.2, background: 'linear-gradient(180deg, #6d000f 0%, #810014 100%)', color: '#fff8ef', position: 'sticky', top: 0, zIndex: 20 }}>
        <IconButton aria-label="Open admin menu" onClick={() => setMobileOpen(true)} sx={{ color: 'inherit' }}>
          <Menu />
        </IconButton>
        <Typography sx={{ fontWeight: 800 }}>Admin Panel</Typography>
        <HeaderProfileMenu
          initial={user?.first_name?.[0] || 'A'}
          actions={[
            { label: 'Admin Dashboard', onClick: () => navigate('/admin') },
            { label: 'User Dashboard', onClick: () => navigate('/my-profile') },
            { label: 'Logout', onClick: handleLogout, icon: <LogoutIcon fontSize="small" />, color: '#b71c1c' },
          ]}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px 1fr' }, minHeight: '100vh' }}>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>{drawer}</Box>

        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', background: 'linear-gradient(180deg, #6d000f 0%, #810014 100%)' } }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, background: 'linear-gradient(180deg, #6d000f 0%, #810014 100%)' }}>
            <IconButton onClick={() => setMobileOpen(false)} sx={{ color: '#fff7ee' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          {drawer}
        </Drawer>

        <Box
          sx={{
            minWidth: 0,
            px: { xs: 1, sm: 1.5, md: 3 },
            py: { xs: 1.25, md: 2.4 },
            overflowX: 'hidden',
            '& .MuiCardContent-root': { px: { xs: 1.25, sm: 2 } },
            '& .MuiTableContainer-root': {
              maxWidth: '100%',
              overflowX: 'auto !important',
              WebkitOverflowScrolling: 'touch',
            },
            '& .MuiTable-root': {
              minWidth: { xs: 720, md: '100%' },
            },
          }}
        >
          <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', mb: 1.2 }}>
            <HeaderProfileMenu
              initial={user?.first_name?.[0] || 'A'}
              actions={[
                { label: 'Admin Dashboard', onClick: () => navigate('/admin') },
                { label: 'User Dashboard', onClick: () => navigate('/my-profile') },
                { label: 'Logout', onClick: handleLogout, icon: <LogoutIcon fontSize="small" />, color: '#b71c1c' },
              ]}
            />
          </Box>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
